import { useEffect, useState } from 'react';
import api, { formatApiErrorDetail } from '../../lib/api';
import { F, Btn } from '../../lib/bricks';
import { PROCORE_RETURN, visible, statusLine, linkLine } from './procoreModel';

const MUTED = 'var(--c-ink-mute)';
const LINE = `1px solid var(--c-hair-s)`;
const BAD = 'var(--c-bad)';
const quiet = { minHeight: 44, padding: '0 14px', fontSize: 13 };
const field = { padding: '10px 12px', minHeight: 44, border: '1px solid var(--c-line)', borderRadius: 2, fontSize: 14, minWidth: 0, width: '100%', boxSizing: 'border-box', fontFamily: F.display, background: 'var(--c-field)', color: 'var(--c-ink)' };
const row = { display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: '0 16px', alignItems: 'center', padding: '12px 0', minHeight: 44 };
const textBtn = { minHeight: 44, background: 'none', border: 'none', padding: 0, color: MUTED, fontSize: 13, cursor: 'pointer', fontFamily: F.display, textDecoration: 'underline' };

function errText(e, fallback) {
  return formatApiErrorDetail(e?.response?.data?.detail) || fallback;
}

function returnNote() {
  try {
    const v = new URLSearchParams(window.location.search).get('procore');
    return (v && PROCORE_RETURN[v]) || '';
  } catch { return ''; }
}

// Project page: connect Procore, pick the project reports go to, disconnect.
export default function ProcorePanel({ projectId, onChanged }) {
  const [st, setSt] = useState(null);
  const [picking, setPicking] = useState(false);
  const [choices, setChoices] = useState(null);
  const [pick, setPick] = useState('');
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState(returnNote);
  const [err, setErr] = useState('');
  const url = `/dev/projects/${projectId}/procore`;

  async function load() {
    try { const r = await api.get(url); setSt(r.data); } catch { setSt(null); }
  }
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line

  async function run(what, fn, fallback) {
    setBusy(what); setErr(''); setNote('');
    try { await fn(); } catch (e) { setErr(errText(e, fallback)); }
    setBusy('');
  }

  const connect = () => run('connect', async () => {
    const r = await api.post('/dev/procore/connect');
    if (r.data && r.data.url) { window.location.assign(r.data.url); return; }
    await load(); setNote(PROCORE_RETURN.connected); onChanged && onChanged();
  }, 'Couldn’t start the Procore connection. Nothing was saved.');

  const openPicker = () => run('list', async () => {
    setPicking(true); setChoices(null);
    const r = await api.get(`${url}/projects`);
    const ps = (r.data && r.data.projects) || [];
    setChoices(ps);
    setPick(String((st && st.link && st.link.procore_project_id) || (ps[0] && ps[0].id) || ''));
  }, 'Couldn’t load your Procore projects. Try again.');

  const save = () => run('save', async () => {
    const r = await api.put(url, { procore_project_id: Number(pick) });
    setSt((s) => ({ ...s, link: r.data.link })); setPicking(false); onChanged && onChanged();
  }, 'Couldn’t link that project. Nothing was changed.');

  const disconnect = () => {
    if (!window.confirm('Disconnect Procore? Reports already sent stay in Procore.')) return;
    run('disconnect', async () => { await api.delete('/dev/procore/connect'); setPicking(false); await load(); onChanged && onChanged(); },
      'Couldn’t disconnect right now.');
  };

  if (!visible(st)) return null;
  const connected = st.status === 'connected';
  const canConnect = st.status === 'disconnected' || st.status === 'needs_reconnect';
  return (
    <div style={{ borderTop: LINE, paddingTop: 16, margin: '4px 0 20px' }} data-testid="procore-panel">
      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 500 }}>Procore</h3>
      <div style={{ ...row, borderBottom: connected ? LINE : 'none' }}>
        <span style={{ minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 14.5 }}>{connected ? 'Connected' : 'Send reports to Procore'}</span>
          <span style={{ display: 'block', fontSize: 12.5, color: MUTED, marginTop: 2 }} data-testid="procore-status">{statusLine(st)}</span>
        </span>
        {canConnect ? <Btn variant="ghost" style={quiet} onClick={connect} disabled={busy === 'connect'} data-testid="procore-connect">{busy === 'connect' ? 'Opening…' : st.status === 'needs_reconnect' ? 'Reconnect' : 'Connect'}</Btn> : null}
      </div>
      {connected ? (
        <div style={{ ...row, borderBottom: LINE }}>
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 14 }}>Reports go to</span>
            <span style={{ display: 'block', fontSize: 12.5, color: MUTED, marginTop: 2, overflowWrap: 'anywhere' }} data-testid="procore-link">{linkLine(st.link)}</span>
          </span>
          {!picking ? <Btn variant="ghost" style={quiet} onClick={openPicker} data-testid="procore-pick">{st.link ? 'Change' : 'Choose project'}</Btn> : null}
        </div>
      ) : null}
      {picking ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', padding: '12px 0', borderBottom: LINE }}>
          {choices === null ? <span style={{ fontSize: 13, color: MUTED }}>Loading Procore projects…</span> : choices.length ? (
            <>
              <select aria-label="Procore project" value={pick} onChange={(e) => setPick(e.target.value)} style={{ ...field, flex: '1 1 220px', width: 'auto' }} data-testid="procore-select">
                {choices.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <Btn variant="primary" style={quiet} onClick={save} disabled={busy === 'save' || !pick} data-testid="procore-save">{busy === 'save' ? 'Linking…' : 'Use this project'}</Btn>
              <Btn variant="ghost" style={quiet} onClick={() => setPicking(false)}>Cancel</Btn>
            </>
          ) : <span style={{ fontSize: 13, color: MUTED }}>No active projects in this Procore company.</span>}
        </div>
      ) : null}
      {connected ? (
        <>
          <p style={{ fontSize: 12.5, color: MUTED, margin: '10px 0 0', lineHeight: 1.5 }}>Open a report and choose Send to Procore. It lands as a PDF in a Bricks site reports folder.</p>
          <button type="button" style={textBtn} onClick={disconnect} disabled={busy === 'disconnect'} data-testid="procore-disconnect">Disconnect Procore</button>
        </>
      ) : null}
      {note ? <p role="status" style={{ fontSize: 13, color: 'var(--c-ink)', margin: '8px 0 0' }}>{note}</p> : null}
      {err ? <p role="alert" style={{ color: BAD, fontSize: 12.5, margin: '8px 0 0' }}>{err}</p> : null}
    </div>
  );
}
